/**
 * design_handoff_domain_links — Domain & links tab of Workspace settings.
 * TabbedSettingsPage already renders the shared "Workspace settings" H1
 * (see other settings-family pages on this branch), so this page starts
 * directly at the Domain card.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppStore } from "../../../../../hooks/redux/app";
import { useCurrentOrganizationStore } from "../../../../../hooks/redux/currentOrganization";
import TabbedSettingsPage from "../../../templates/TabbedSettingsPage/TabbedSettingsPage";
import { WORKSPACE_SETTINGS_TABS } from "../../../templates/TabbedSettingsPage/settingsTabs";
import appConfig from "../../../../../appConfig";
import ConfirmDialog from "../../../theme/untitled/ConfirmDialog/ConfirmDialog";
import Icon from "../../../theme/untitled/icons/Icon";
import DomainCard from "./DomainCard";
import MajorToast from "../../../component/Toast/MajorToast";

/**
 * currentOrganization.domain (from the shared reducer's getDomain()) has no
 * "custom" case — it falls through and returns undefined once a custom
 * domain is fully verified. Computed locally here rather than fixing the
 * shared reducer, since every other consumer of that reducer only ever
 * reads it for "default"/"subdomain" org types.
 */
function getLiveHost(model) {
  switch (model.domain_type) {
    case "default":
      return appConfig.defaultPublicDomain;
    case "subdomain":
      return model.subdomain;
    case "custom":
      return model.domain;
    default:
      return model.domain || appConfig.defaultPublicDomain;
  }
}

const OrganizationCustomDomainPage = () => {
  const { clearErrors } = useAppStore();
  const {
    currentOrganization,
    updateDomainSettings,
    refreshDnsStatus,
    regenerateCertificate,
  } = useCurrentOrganizationStore();

  const liveHost = useMemo(
    () => getLiveHost(currentOrganization.model),
    [currentOrganization.model]
  );

  const [selectedOption, setSelectedOption] = useState(
    currentOrganization.model.domain_type
  );
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const isRefreshing = currentOrganization.refreshDNSStatus;
  const isRegenerating = currentOrganization.regenerateCertificate;
  // Disconnect reuses updateDomainSettings("default_domain") under the hood,
  // so its loading state is the same flag as the default-domain save.
  const isDisconnecting = currentOrganization.updateDomain;
  const isLoading =
    currentOrganization.updateDomain || isRefreshing || isRegenerating;

  const dnsSettings = useMemo(
    () =>
      currentOrganization.model.domain_dns ?? {
        validationCName: "",
        validationCNameValue: "",
        validationCNameStatus: "pending",
        validationCNameError: null,
        routingCName: "",
        routingCNameValue: "",
        routingCNameStatus: "pending",
        routingCNameError: null,
      },
    [currentOrganization.model.domain_dns]
  );

  // Tracks which domain type was active immediately before a
  // "default_domain" submit, so the success toast can distinguish
  // "removed subdomain" from "disconnected custom domain" — by the time
  // the success effect runs, domain_type has already flipped to "default"
  // and that distinction is otherwise lost.
  const revertedFromRef = useRef(null);

  useEffect(() => {
    clearErrors();
  }, []);

  useEffect(() => {
    if (currentOrganization.updateDomainSuccess) {
      const domainType = currentOrganization.model.domain_type;
      const pendingDomain = currentOrganization.model.pending_dns_verification;

      let content = null;
      if (domainType === "subdomain") {
        content = {
          title: "Subdomain set",
          body: (
            <>
              Your widgets are now on{" "}
              <strong>{currentOrganization.model.subdomain}</strong>.
            </>
          ),
        };
      } else if (domainType === "default") {
        content =
          revertedFromRef.current === "custom"
            ? {
                title: "Domain disconnected",
                body: (
                  <>
                    Your widgets are back on{" "}
                    <strong>{appConfig.defaultPublicDomain}</strong>.
                  </>
                ),
              }
            : {
                title: "Subdomain removed",
                body: (
                  <>
                    Your widgets are back on{" "}
                    <strong>{appConfig.defaultPublicDomain}</strong>.
                  </>
                ),
              };
        revertedFromRef.current = null;
      } else if (pendingDomain) {
        content = {
          title: "Domain saved",
          body: (
            <>
              Add the two DNS records below to finish setup. Links stay on{" "}
              <strong>{liveHost}</strong> until then.
            </>
          ),
        };
      }

      if (content) setToast(content);
      setPendingConfirm(null);
    }
  }, [currentOrganization.updateDomainSuccess]);

  useEffect(() => {
    if (currentOrganization.updateDomainError) {
      setPendingConfirm(null);
    }
  }, [currentOrganization.updateDomainError]);

  useEffect(() => {
    if (currentOrganization.regenerateCertificateSuccess) {
      setToast({
        title: "Certificate regenerated",
        body: "Update the DNS record with the new value shown below.",
      });
    }
  }, [currentOrganization.regenerateCertificateSuccess]);

  const isVerificationPending = useMemo(
    () =>
      currentOrganization.model.pending_dns_verification !== null &&
      currentOrganization.model.pending_dns_verification !== undefined &&
      String(currentOrganization.model.pending_dns_verification).length > 0,
    [currentOrganization.model.pending_dns_verification]
  );

  useEffect(() => {
    if (isVerificationPending) {
      setSelectedOption("custom");
    }
  }, [isVerificationPending]);

  const onSelectOption = useCallback(
    (option) => () => setSelectedOption(option),
    []
  );

  const closePendingConfirm = useCallback(() => {
    if (isLoading) return;
    setPendingConfirm(null);
  }, [isLoading]);

  const onRequestRevert = useCallback(() => {
    setPendingConfirm({ kind: "revert" });
  }, []);

  const onRequestSubdomainSubmit = useCallback((fullHost, isReplacing) => {
    if (isReplacing) {
      setPendingConfirm({ kind: "subdomain", value: fullHost });
    } else {
      updateDomainSettings("subdomain", fullHost);
    }
  }, [updateDomainSettings]);

  const onRequestCustomDomainSubmit = useCallback((domainValue, isReplacing) => {
    if (isReplacing) {
      setPendingConfirm({ kind: "custom", value: domainValue });
    } else {
      updateDomainSettings("custom", domainValue);
    }
  }, [updateDomainSettings]);

  const onRequestDisconnect = useCallback(() => {
    setPendingConfirm({ kind: "disconnect" });
  }, []);

  const onConfirm = useCallback(() => {
    if (!pendingConfirm) return;

    switch (pendingConfirm.kind) {
      case "revert": {
        const hasPendingDomain = !!currentOrganization.model.pending_dns_verification;
        revertedFromRef.current = hasPendingDomain
          ? "custom"
          : currentOrganization.model.domain_type;
        updateDomainSettings("default_domain");
        break;
      }
      case "subdomain":
        updateDomainSettings("subdomain", pendingConfirm.value);
        break;
      case "custom":
        updateDomainSettings("custom", pendingConfirm.value);
        break;
      case "disconnect":
        revertedFromRef.current = "custom";
        updateDomainSettings("default_domain");
        setSelectedOption("default");
        break;
      default:
        break;
    }
  }, [pendingConfirm, currentOrganization.model, updateDomainSettings]);

  const onRefreshDnsStatus = useCallback(() => {
    refreshDnsStatus();
  }, [refreshDnsStatus]);

  const onRegenerate = useCallback(() => {
    regenerateCertificate();
  }, [regenerateCertificate]);

  const onDismissToast = useCallback(() => setToast(null), []);

  const defaultOptionProps = {
    domainType: currentOrganization.model.domain_type,
    isVerificationPending,
    pendingDomain: currentOrganization.model.pending_dns_verification || null,
    onRequestRevert,
    isLoading,
    error: currentOrganization.updateDomainError,
  };

  const subdomainOptionProps = {
    subDomain: currentOrganization.model.subdomain || "",
    onRequestSubmit: onRequestSubdomainSubmit,
    isLoading,
    error: currentOrganization.updateDomainError,
  };

  const customOptionProps = {
    pendingDomain: currentOrganization.model.pending_dns_verification || null,
    domain: currentOrganization.model.domain || null,
    onRequestSubmit: onRequestCustomDomainSubmit,
    isLoading,
    error: currentOrganization.updateDomainError,
    dnsSettings,
    isRefreshing,
    isRegenerating,
    isDisconnecting,
    onRefreshDnsStatus,
    onRegenerate,
    onRequestDisconnect,
    refreshError: currentOrganization.refreshDNSStatusError,
    regenerateError: currentOrganization.regenerateCertificateError,
    disconnectError: currentOrganization.updateDomainError,
  };

  const confirmContent = useConfirmContent(pendingConfirm, currentOrganization, liveHost);

  return (
    <TabbedSettingsPage
      title="Workspace settings"
      subtitle={
        currentOrganization.model.name &&
        `Applies to ${currentOrganization.model.name}. Other workspaces are unaffected.`
      }
      tabs={WORKSPACE_SETTINGS_TABS}
    >
      <DomainCard
        liveHost={liveHost}
        pendingDomain={currentOrganization.model.pending_dns_verification || null}
        currentPlan={currentOrganization.subscription.plan_code}
        selectedOption={selectedOption}
        onSelectOption={onSelectOption}
        defaultOptionProps={defaultOptionProps}
        subdomainOptionProps={subdomainOptionProps}
        customOptionProps={customOptionProps}
      />

      {confirmContent && (
        <ConfirmDialog
          open={!!pendingConfirm}
          onClose={closePendingConfirm}
          onConfirm={onConfirm}
          variant={confirmContent.variant}
          icon={<Icon name="AlertTriangle" size={24} />}
          title={confirmContent.title}
          confirmLabel={isLoading ? "Saving…" : confirmContent.confirmLabel}
          cancelLabel={confirmContent.cancelLabel}
          busy={isLoading}
        >
          {confirmContent.body}
        </ConfirmDialog>
      )}

      {toast && (
        <MajorToast title={toast.title} text={toast.body} onDismiss={onDismissToast} />
      )}
    </TabbedSettingsPage>
  );
};

/**
 * Copy for the themed confirm dialog, keyed by pendingConfirm.kind. Only
 * "revert" is spelled out verbatim in the spec ("Switch to the default
 * domain?"); the other three destructive switches follow the same shape
 * per its own "never native confirm() for any destructive switch" rule.
 */
function useConfirmContent(pendingConfirm, currentOrganization, liveHost) {
  return useMemo(() => {
    if (!pendingConfirm) return null;

    const defaultHost = appConfig.defaultPublicDomain;
    const pendingDomain = currentOrganization.model.pending_dns_verification;

    switch (pendingConfirm.kind) {
      case "revert":
        // While a custom domain is only pending (not yet promoted),
        // domain_type — and so the live host — is still "default": nothing
        // about the live address actually changes, only the in-progress
        // setup is cancelled. liveHost === defaultHost in that case, so the
        // "links move from X to X" phrasing below would be nonsensical.
        return pendingDomain
          ? {
              variant: "warning",
              title: "Cancel custom domain setup?",
              confirmLabel: "Cancel setup",
              cancelLabel: "Keep setting up",
              body: (
                <p>
                  Your custom domain setup for <strong>{pendingDomain}</strong> will be
                  cancelled and its DNS records removed. Links stay on{" "}
                  <strong>{liveHost}</strong>.
                </p>
              ),
            }
          : {
              variant: "warning",
              title: "Switch to the default domain?",
              confirmLabel: "Switch to default",
              cancelLabel: "Keep current domain",
              body: (
                <p>
                  Links move from <strong>{liveHost}</strong> to{" "}
                  <strong>{defaultHost}</strong>. Old links are not redirected — QR codes and
                  shared URLs on the old address stop working.
                </p>
              ),
            };
      case "subdomain":
        return {
          variant: "warning",
          title: "Change your subdomain?",
          confirmLabel: "Change subdomain",
          cancelLabel: "Keep current subdomain",
          body: (
            <p>
              Links move from <strong>{liveHost}</strong> to{" "}
              <strong>{pendingConfirm.value}</strong>. Old links are not redirected — QR codes
              and shared URLs on the old address stop working.
            </p>
          ),
        };
      case "custom":
        return {
          variant: "warning",
          title: "Change your domain?",
          confirmLabel: "Change domain",
          cancelLabel: "Keep current domain",
          body: (
            <p>
              Links move from <strong>{liveHost}</strong> to{" "}
              <strong>{pendingConfirm.value}</strong> once the new domain is verified. Old links
              are not redirected.
            </p>
          ),
        };
      case "disconnect": {
        // Same "nothing is actually live yet" case as "revert" above.
        // model.domain can already be pre-populated with the candidate
        // domain even while it's still pending/unverified (confirmed via
        // the real API payload — domain_type stays "default"/"subdomain"
        // until promoted), so pendingDomain is the only reliable signal
        // here, not model.domain's truthiness.
        const activeDomain = pendingDomain || currentOrganization.model.domain;

        return pendingDomain
          ? {
              variant: "warning",
              title: "Cancel custom domain setup?",
              confirmLabel: "Cancel setup",
              cancelLabel: "Keep setting up",
              body: (
                <p>
                  Your custom domain setup for <strong>{activeDomain}</strong> will be
                  cancelled and its DNS records removed. Links stay on{" "}
                  <strong>{liveHost}</strong>.
                </p>
              ),
            }
          : {
              variant: "destructive",
              title: "Disconnect this domain?",
              confirmLabel: "Disconnect domain",
              cancelLabel: "Cancel",
              body: (
                <p>
                  Links move from <strong>{activeDomain}</strong> to{" "}
                  <strong>{defaultHost}</strong>. You&rsquo;ll need to redo DNS setup if you
                  reconnect this domain later.
                </p>
              ),
            };
      }
      default:
        return null;
    }
  }, [pendingConfirm, currentOrganization.model, liveHost]);
}

export default OrganizationCustomDomainPage;
